import { ChangeEvent, useEffect, useState, FocusEvent, Dispatch, useCallback, MouseEvent } from 'react';
import debounce from 'lodash.debounce';
import { connect, useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';
import { FormSpy } from 'react-final-form';
import spacetime from 'spacetime';

import { AutoComplete } from 'UIPackage/MainModule/components/AutoComplete';
import { IKnmIndex } from 'APIPackage/KnmModule/interfaces/IKnmIndex';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { formatDateLocale } from 'CorePackage/MainModule/common/formatDate';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { knmListFindKnm, knmListSetFindData } from 'KnmListPackage/MainModule/actions/knmListActions';
import { getKnmListFindData } from 'KnmListPackage/MainModule/selectors/knmListSelectors';

import styles from './ErknmReasonCancelledLink.module.scss';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { IKnmAbrogationForm } from '../../../APIPackage/KnmModule/interfaces/IKnmAbrogationForm';
import { ROUTE_PM } from '../../../CorePackage/MainModule/routes/routes';
import { CloseButton } from '../../../UIPackage/MainModule/components/CloseButton';
import { ABROGATION_LINK_BLOCK_ID } from '../../MainModule/constants/knmErknmFieldsElementsIds';
import { fetchKnm } from '../../../APIPackage/KnmModule/services/knmApi';
import { IKnm } from '../../../APIPackage/KnmModule/interfaces/IKnm';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { fetchDictionaryValue } from '../../../APIPackage/DictionariesModule/services/dictionariesApi';
import { IDictionaryVersion } from '../../../APIPackage/DictionariesModule/interfaces/IDictionaryVersion';
import { IKnmType } from '../../../APIPackage/CatalogsModule/interfaces/IKnmType';
import { DictionaryUUIDsEnum } from '../../../CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { IKnmErvk } from '../../../APIPackage/CatalogsModule/interfaces/IKnmErvk';

const ERP_ID_MIN_LENGTH = 3;

interface IKnmAutocompleteOption {
  label: string;
  value: string;
  knm: IKnmIndex;
}

const generateKnmInfoText = (knm: IKnmIndex) =>
  `${knm.erpId ?? ''} ${knm.knmType ?? ''} ${knm.organizationName ?? ''} ${knm.inn ? `(ИНН: ${knm.inn}` : ''}${
    knm.ogrn ? ` ОГРН: ${knm.ogrn}` : ''
  }) ${knm.controllingOrganization ?? ''}${knm.prosecutorOrganization ?? ''} ${formatDateLocale({
    date: knm.startDate,
    dateFormat: DATE_FORMAT_LONG_FNS,
  })} - ${knm.stopDate ? `${formatDateLocale({ date: knm.stopDate, dateFormat: DATE_FORMAT_LONG_FNS })}` : '?'}`;

interface IErknmLinksProps {
  watchOnly: boolean;
  isEnabled: boolean;
  abrogation: IKnmAbrogationForm;
}

interface IErknmLinksStoreProps {
  formRef: IKnmFormRef | undefined;
  knms: IKnmIndex[];
}

const ErknmReasonCancelledLinkWithoutStore = ({
  watchOnly,
  isEnabled,
  formRef,
  knms,
  abrogation,
}: IErknmLinksProps & IErknmLinksStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [isAddingFieldsActive, setIsAddingFieldsActive] = useState(false);
  const [knmsAutocompleteOptions, setKnmsAutocompleteOptions] = useState<IKnmAutocompleteOption[] | undefined>();
  const [erpIdInputValue, setErpIdInputValue] = useState('');
  const [isErpIdSelected, setIsErpIdSelected] = useState(false);
  const [lastKnmsAutocompleteOptions, setLastKnmsAutocompleteOptions] = useState<IKnmAutocompleteOption[]>([]);
  const [isFounded, setIsFounded] = useState(false);

  const closeLinkFields = useCallback(() => {
    setIsAddingFieldsActive(false);
    setErpIdInputValue('');
    formRef?.current?.change('abrogation', { ...abrogation, link: undefined, pmObject: undefined });
  }, [abrogation]);

  function findKnms(paramErpId?: string) {
    if (paramErpId && paramErpId.length >= ERP_ID_MIN_LENGTH) {
      setIsFounded(true);

      const knoOrganizationErknm = formRef?.current.getFieldState('knoOrganizationErknm')?.value;
      const kindControl = formRef?.current.getFieldState('kindControl')?.value;
      const startDate = spacetime(formRef?.current.getFieldState('startDate')?.value || '');

      dispatch(
        knmListFindKnm(
          {
            erpId: paramErpId,
            pm: true,
            statusIds: [7],
            controllingOrganizationIds: [knoOrganizationErknm?.idStr?.toString() || ''],
            supervisionTypeId: kindControl?.code?.toString() || '',
            years: [startDate.year()],
          },
          2000,
        ),
      );
    }
  }

  const debouncedGetKnms = debounce(findKnms, 1500);

  const handleTextInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const valueEdited = value?.trim();

      setErpIdInputValue(valueEdited);
      if (!valueEdited) {
        setIsErpIdSelected(false);
      }
      debouncedGetKnms(valueEdited);
      if (!valueEdited || valueEdited.length <= ERP_ID_MIN_LENGTH) {
        setKnmsAutocompleteOptions(undefined);
      }
    },
    [debouncedGetKnms],
  );

  const handleAutoCompleteSelect = useCallback(
    (value: string) => () => {
      setErpIdInputValue(value);
      debouncedGetKnms('');
      setKnmsAutocompleteOptions(undefined);
      setIsErpIdSelected(true);
    },
    [debouncedGetKnms],
  );

  useEffect(() => {
    if (!isAddingFieldsActive) {
      setErpIdInputValue('');
      setIsErpIdSelected(false);
    }
  }, [isAddingFieldsActive, formRef]);

  useEffect(() => {
    isFounded &&
      setKnmsAutocompleteOptions(
        knms.map(knm => {
          const knmText = generateKnmInfoText(knm);

          return {
            value: knmText,
            label: knmText,
            knm,
          };
        }),
      );
  }, [knms, isFounded]);

  useUnmount(() => {
    dispatch(knmListSetFindData([]));
  });

  useEffect(() => {
    knmsAutocompleteOptions && setLastKnmsAutocompleteOptions(knmsAutocompleteOptions);
  }, [knmsAutocompleteOptions]);

  const handleTextInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const tagName = (e?.relatedTarget as Element)?.tagName;

      if (tagName !== 'UL') {
        debouncedGetKnms('');
        setKnmsAutocompleteOptions(undefined);
      }
    },
    [debouncedGetKnms],
  );

  const handleTextInputClear = useCallback(() => {
    setErpIdInputValue('');
    setIsErpIdSelected(false);
  }, []);

  const addLink = useCallback(
    async (knm: IKnmIndex) => {
      const pmObject: { pmData?: IKnm; pmTypeValue?: IKnmType; ervkValue?: IKnmErvk } = {};
      const pmData: IKnm = await fetchKnm(knm.id);
      let pmType: IDictionaryVersion | undefined;
      let pmTypeValue: IKnmType | undefined;
      let ervkDictionary: IDictionaryVersion | undefined;
      let ervkValue: IKnmErvk | undefined;

      if (pmData.pmErknm && pmData.pmErknm?.pmType) {
        pmType = await fetchDictionaryValue(pmData.pmErknm.pmType.dictId, pmData.pmErknm.pmType.dictVersionId);
      }

      if (pmType) {
        pmTypeValue = JSON.parse(pmType.value);
      }

      if (
        pmData.pmErknm?.reasons &&
        pmData.pmErknm?.reasons.length &&
        pmData.pmErknm?.reasons[0].reason &&
        pmData.pmErknm?.reasons[0].reason.ervkUuid
      ) {
        ervkDictionary = await fetchDictionaryValue(
          DictionaryUUIDsEnum.ERVK,
          pmData.pmErknm?.reasons[0].reason.ervkUuid,
        );
      }

      if (ervkDictionary) {
        ervkValue = JSON.parse(ervkDictionary.value);
      }

      pmObject.pmData = pmData;
      pmObject.pmTypeValue = pmTypeValue;
      pmObject.ervkValue = ervkValue;

      formRef?.current?.change('abrogation', {
        ...abrogation,
        link: { erpId: knm.erpId, id: knm.id, guid: null },
        pmObject,
      });

      setErpIdInputValue('');
      setIsErpIdSelected(false);
    },
    [abrogation],
  );

  const removeLink = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      formRef?.current?.change('abrogation', { ...abrogation, link: undefined, pmObject: undefined });
      setErpIdInputValue('');
      setIsErpIdSelected(false);
    },
    [abrogation],
  );

  const handleSaveButtonClick = useCallback(() => {
    const knmAutocompleteOption = lastKnmsAutocompleteOptions?.find(
      lastKnmAutocompleteOption => lastKnmAutocompleteOption.value === erpIdInputValue,
    );
    const knm = knmAutocompleteOption?.knm;

    if (knm) {
      addLink(knm);
    }
  }, [addLink, erpIdInputValue, lastKnmsAutocompleteOptions]);

  /**
   * Переход по ссылке на паспорт
   */
  const onClickProphylacticVisit = useCallback(() => {
    window.open(ROUTE_PM(abrogation?.link?.id || ''), '_blank');
  }, [abrogation]);

  return (
    <>
      <div>
        <div className={abrogation?.link?.erpId ? styles.LinkFieldsFill : styles.LinkFields}>
          <FieldBlock
            id={ABROGATION_LINK_BLOCK_ID}
            label="Ссылка на профилактический визит"
            placementLabel="left"
            color="black"
          >
            {abrogation?.link?.erpId && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div className={styles.ResultBlock} onClick={onClickProphylacticVisit}>
                  <span>{abrogation?.link?.erpId}</span>
                  <CloseButton className="shared-deleting-row-btn" onClick={watchOnly ? () => null : removeLink} />
                </div>
                <FormSpy subscription={{ errors: true, touched: true }}>
                  {props => {
                    const errorMessage: string | undefined = props.errors?.abrogation?.link;

                    if (errorMessage) {
                      return <p className="shared-input-error shared-row__small-margin">{errorMessage}</p>;
                    }

                    return null;
                  }}
                </FormSpy>
              </div>
            )}
            <div className={abrogation?.link?.erpId ? styles.HideInputLink : styles.AutoCompleteStyle}>
              <AutoComplete
                type="area"
                options={knmsAutocompleteOptions}
                noFoundMessage="ПМ соответствующие указанному учетному номеру не найдены"
                handleAutoCompleteSelect={handleAutoCompleteSelect}
              >
                {isErpIdSelected && (
                  <div className={styles.ResultBlockNotAdd}>
                    <span>{erpIdInputValue.split(' ')[0]}</span>
                    <CloseButton className="shared-deleting-row-btn" onClick={handleTextInputClear} />
                  </div>
                )}
                <TextInputField
                  id="abrogation.link"
                  name="abrogation.link"
                  placeholder="Введите учетный номер ПМ"
                  clearable={isErpIdSelected}
                  className={abrogation?.link?.erpId || isErpIdSelected ? styles.HideInputLink : undefined}
                  value={erpIdInputValue}
                  onBlur={handleTextInputBlur}
                  onChange={handleTextInputChange}
                  onClear={handleTextInputClear}
                />
              </AutoComplete>
            </div>
          </FieldBlock>
          {!abrogation?.link?.erpId && (
            <>
              <Button
                className={styles.SaveButton}
                text="Сохранить"
                color="default"
                size="medium"
                type="button"
                id="saveButton"
                disabled={watchOnly || !isErpIdSelected || !isEnabled}
                onClick={handleSaveButtonClick}
              />
              <Button
                className={styles.CancelButton}
                variant="link"
                color="primary"
                text="Отмена"
                size="medium"
                type="button"
                onClick={closeLinkFields}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmLinksStoreProps => ({
  formRef: getKnmFormRef(state),
  knms: getKnmListFindData(state),
});

export const ErknmReasonCancelledLink = connect(mapStateToProps)(ErknmReasonCancelledLinkWithoutStore);
